import { Client } from '@modelcontextprotocol/sdk/client/index.js';
import { StreamableHTTPClientTransport } from '@modelcontextprotocol/sdk/client/streamableHttp.js';

import { ENV } from '../config/env.js';

import type { TokenProvider } from './client.js';

/**
 * MCP-over-HTTP client. One transport per browser session — the SDK
 * manages `mcp-session-id` for us, so callers just `connect()` once and
 * reuse the returned client for every tool call. The token provider is
 * invoked per request, so silent token refresh is transparent.
 */
export interface McpClientHandle {
  readonly client: Client;
  close(): Promise<void>;
}

export async function createMcpClient(
  getToken: TokenProvider,
  baseUrl = ENV.apiBaseUrl,
): Promise<McpClientHandle> {
  const url = new URL('/mcp', baseUrl);
  const transport = new StreamableHTTPClientTransport(url, {
    requestInit: { headers: {} },
    fetch: async (input, init): Promise<Response> => {
      const token = await getToken();
      const headers = new Headers(init?.headers);
      headers.set('Authorization', `Bearer ${token}`);
      return fetch(input, { ...init, headers });
    },
  });
  const client = new Client({ name: 'wt-codegraph-web', version: '0.0.0' }, { capabilities: {} });
  await client.connect(transport);
  return {
    client,
    close: () => client.close(),
  };
}

/** Minimal call helper — every MCP tool returns a `content` array of blocks. */
export async function callTool(
  handle: McpClientHandle,
  name: string,
  args: Record<string, unknown>,
): Promise<unknown> {
  const result = await handle.client.callTool({ name, arguments: args });
  return result;
}
